import React, { useState } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PowerDialerCall, usePowerDialer } from "@/context/PowerDialerContext";
import { DialingCallItem } from "@/components/dialer/components/DialingCallItem";
import { LiveCallCard } from "@/components/dialer/components/LiveCallCard";
import { QueueGroupSection } from "@/components/dialer/power-dialer/QueueGroupSection";
import { RequeueOutcomesDialog } from "@/components/dialer/power-dialer/RequeueOutcomesDialog";
import { useGetDialerQueueCountsQuery } from "@/store/rest_api";
import { DialerQueueGroup } from "@/enum/DialerQueueGroup";

const QUEUE_GROUPS: { group: DialerQueueGroup; label: string }[] = [
  { group: DialerQueueGroup.DUE_NOW, label: "Up next" },
  { group: DialerQueueGroup.RETRY_LATER, label: "Retry later" },
  { group: DialerQueueGroup.OUTSIDE_CALLING_HOURS, label: "Outside calling hours" },
  { group: DialerQueueGroup.DAILY_CAP, label: "Daily call limit" },
  { group: DialerQueueGroup.SKIPPED, label: "Skipped" },
];

const COUNTS_REFRESH_INTERVAL_MS = 15_000;

function isBridged(call: PowerDialerCall): boolean {
  return call.status === "CONNECTED" || call.status === "ON_HOLD";
}

export default function PowerDialerRail() {
  const { campaign, lineCalls, lines, isPreview } = usePowerDialer();
  const campaignId = campaign?.id ?? "";
  const { data: counts } = useGetDialerQueueCountsQuery(campaignId, {
    skip: !campaignId,
    pollingInterval: COUNTS_REFRESH_INTERVAL_MS,
  });
  const [isRequeueOpen, setIsRequeueOpen] = useState(false);

  const total = counts?.total ?? 0;
  const dialed = counts?.dialed ?? 0;
  const progress = total > 0 ? (dialed / total) * 100 : 0;
  const orderedLineCalls = [...lineCalls].sort((first, second) => Number(isBridged(second)) - Number(isBridged(first)));
  const dialingLines = Math.min(lines, counts?.pacedLines ?? lines);
  const slotCount = Math.max(isPreview ? 1 : dialingLines, orderedLineCalls.length);
  const slots = Array.from({ length: slotCount }, (_, index) => orderedLineCalls[index]);

  return (
    <aside className="flex h-full w-80 shrink-0 flex-col border-r border-border bg-muted/60">
      <div className="shrink-0 p-5">
        <Progress value={progress} className="h-2 bg-border" />
        <div className="mt-2.5 flex justify-between text-sm tabular-nums text-muted-foreground">
          <span>{dialed} dialed</span>
          <span>{total} total</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-4">
        <div>
          <h4 className="mb-2.5 px-1 text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Lines
          </h4>
          {!isPreview && dialingLines > 1 && counts && (
            <p className="mb-2 px-1 text-sm text-muted-foreground">
              {counts.writtenConsentQueued} queued {counts.writtenConsentQueued === 1 ? "number has" : "numbers have"}{" "}
              written consent for extra lines
            </p>
          )}
          <div className="space-y-1.5">
            {slots.map((call, index) => {
              if (!call) {
                return (
                  <div
                    key={`idle-line-${index}`}
                    className="flex items-center gap-3 rounded-md px-1 py-2.5 text-sm text-muted-foreground"
                  >
                    <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
                    Line {index + 1} · Idle
                  </div>
                );
              }
              if (isBridged(call)) {
                return <LiveCallCard key={call.id} call={call} />;
              }
              return <DialingCallItem key={call.id} call={call} />;
            })}
          </div>
        </div>

        {campaignId && (
          <div className="space-y-4">
            {QUEUE_GROUPS.map(({ group, label }) => (
              <QueueGroupSection
                key={group}
                campaignId={campaignId}
                group={group}
                label={label}
                count={counts?.groups[group] ?? 0}
                defaultOpen={group === DialerQueueGroup.DUE_NOW}
              />
            ))}
          </div>
        )}
      </div>

      {campaignId && (
        <div className="max-h-56 shrink-0 overflow-y-auto border-t border-border px-4 py-4">
          <QueueGroupSection
            campaignId={campaignId}
            group={DialerQueueGroup.DONE}
            label="Done"
            count={counts?.groups[DialerQueueGroup.DONE] ?? 0}
          />
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 w-full justify-start px-1 text-sm text-muted-foreground"
            onClick={() => setIsRequeueOpen(true)}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Requeue leads
          </Button>
          <RequeueOutcomesDialog campaignId={campaignId} open={isRequeueOpen} onOpenChange={setIsRequeueOpen} />
        </div>
      )}
    </aside>
  );
}
